import { dayFromParts, dayOfDateOnly } from '@/lib/time';

import { ORIGIN_BY_LABEL, STATUS_BY_LABEL } from './columns';

// Conversión de una celda del Excel al valor del contrato. Cada función devuelve el
// valor, `undefined` si la celda está vacía (campo opcional) o un error en palabras.

export type Cell = string | number | boolean | Date | null;
export type Parsed<T> = { ok: T | undefined } | { error: string };

export const hasControl = (s: string): boolean =>
  [...s].some((ch) => (ch.codePointAt(0) ?? 0) < 0x20);
const SINGLE_LINE = 'No puede tener saltos de línea ni tabuladores.';

export function text(cell: Cell): string | undefined {
  if (cell === null) {
    return undefined;
  }
  const s = (cell instanceof Date ? cell.toISOString() : String(cell)).trim();
  return s === '' ? undefined : s;
}

/** Texto con el largo máximo del servidor; `singleLine` rechaza saltos y tabuladores. */
export function limited(cell: Cell, max: number, singleLine = false): Parsed<string> {
  const s = text(cell);
  if (s === undefined) {
    return { ok: undefined };
  }
  if (singleLine && hasControl(s)) {
    return { error: SINGLE_LINE };
  }
  return s.length > max ? { error: `Máximo ${max} caracteres.` } : { ok: s };
}

/** Etiqueta de revisión: si Excel la volvió fecha ("1-2"), se pide escribirla como texto. */
export function revision(cell: Cell): Parsed<string> {
  if (cell instanceof Date) {
    return {
      error:
        'Excel convirtió la revisión en fecha. Dale formato de texto a la columna y escríbela de nuevo.',
    };
  }
  return limited(cell, 20);
}

export function positiveInt(cell: Cell): Parsed<number> {
  const s = text(cell);
  if (s === undefined) {
    return { ok: undefined };
  }
  const n = Number(s);
  return Number.isInteger(n) && n >= 1
    ? { ok: n }
    : { error: 'Debe ser un número entero de 1 en adelante.' };
}

/** Fecha de celda (el Excel la entrega como Date) o texto DD/MM/AAAA o AAAA-MM-DD. */
export function date(cell: Cell): Parsed<string> {
  if (cell instanceof Date) {
    const day = dayOfDateOnly(cell);
    return day === null ? { error: 'Fecha no válida. Usa el formato DD/MM/AAAA.' } : { ok: day };
  }
  const s = text(cell);
  if (s === undefined) {
    return { ok: undefined };
  }
  const dmy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  const iso = dmy
    ? dayFromParts(Number(dmy[3]), Number(dmy[2]), Number(dmy[1]))
    : ymd
      ? dayFromParts(Number(ymd[1]), Number(ymd[2]), Number(ymd[3]))
      : null;
  return iso === null ? { error: 'Fecha no válida. Usa el formato DD/MM/AAAA.' } : { ok: iso };
}

function fromLabels(map: Record<string, string>, error: string): (cell: Cell) => Parsed<string> {
  return (cell) => {
    const s = text(cell);
    if (s === undefined) {
      return { ok: undefined };
    }
    const v = map[s.toLowerCase()];
    return v === undefined ? { error } : { ok: v };
  };
}

export const status = fromLabels(
  STATUS_BY_LABEL,
  'Valor no reconocido. Usa: Reservado, Borrador, En revisión, En aprobación, Vigente u Obsoleto.',
);
export const origin = fromLabels(ORIGIN_BY_LABEL, 'Valor no reconocido. Usa: Interno o Externo.');

/** "GC / AUD" → ["GC","AUD"]. Con `strict`, un nivel vacío es error (ruta de códigos). */
export function path(cell: Cell, strict: boolean): Parsed<string[]> {
  const s = text(cell);
  if (s === undefined) {
    return { ok: undefined };
  }
  const parts = s.split('/').map((p) => p.trim());
  if (strict && parts.some((p) => p === '')) {
    return {
      error: 'Escribe los códigos de proceso separados por "/", sin niveles vacíos: "GC / AUD".',
    };
  }
  if (strict && parts.length > 8) {
    return { error: 'La ruta admite hasta 8 niveles de proceso.' };
  }
  if (strict && parts.some((p) => p.length > 40 || hasControl(p))) {
    return { error: 'Cada código de proceso admite hasta 40 caracteres.' };
  }
  if (!strict && parts.some((p) => p.length > 160)) {
    return { error: 'Cada nombre de proceso admite hasta 160 caracteres.' };
  }
  return { ok: strict ? parts : parts.filter((p) => p !== '') };
}

/** "ISO 9001 8.6; BPADIS L-14": la cláusula es la última palabra de cada norma. */
export function norms(cell: Cell): Parsed<{ normCode: string; clause: string }[]> {
  const s = text(cell);
  if (s === undefined) {
    return { ok: undefined };
  }
  const out: { normCode: string; clause: string }[] = [];
  for (const item of s
    .split(';')
    .map((p) => p.trim())
    .filter((p) => p !== '')) {
    if (item.includes(',')) {
      return {
        error: 'Separa las normas con punto y coma (;): "ISO 9001 8.6; ISO 9001 8.7".',
      };
    }
    const cut = item.lastIndexOf(' ');
    if (cut <= 0) {
      return {
        error:
          'Escribe cada norma con su cláusula, separadas por punto y coma: "ISO 9001 8.6; BPADIS L-14".',
      };
    }
    const ref = { normCode: item.slice(0, cut).trim(), clause: item.slice(cut + 1).trim() };
    if (ref.normCode.length > 60 || ref.clause.length > 60 || hasControl(item)) {
      return { error: 'Cada norma y cláusula admite hasta 60 caracteres.' };
    }
    out.push(ref);
  }
  if (out.length > 50) {
    return { error: 'Una fila admite hasta 50 normas.' };
  }
  return { ok: out };
}
